import { MenuProps } from '@chakra-ui/react';
import { Children, ComponentProps, FC, isValidElement, MouseEvent, ReactElement, ReactNode, useLayoutEffect, useRef, useState } from 'react';

import { Box, Button, HStack, Menu, MenuButton, MenuItem, MenuList, Portal, rem, Text, Tooltip, VStack } from '@bonusly/design-system';

import { useCloseNavbarPanels } from '../../../hooks/use-navbar-panels';

/**
 * A MenuItem wrapper that only renders if its children have content.
 * This handles children wrapped in ProtectedContent/FeatureFlag/Reveal/Conceal that may render null.
 * We render the children first, check if they produced any DOM elements, then
 * conditionally render the MenuItem wrapper.
 */
const ConditionalMenuItem: FC<ComponentProps<typeof MenuItem> & { children: ReactNode }> = ({ children, ...menuItemProps }) => {
	const [hasContent, setHasContent] = useState(true);
	const contentRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		// Check if the rendered children produced any DOM elements
		// Re-evaluate when children change (e.g., when async data like nominationsOpen changes)
		const hasChildElements = contentRef.current?.firstElementChild != null;
		setHasContent(hasChildElements);
	}, [children]);

	// If no content was rendered, don't render the MenuItem wrapper
	if (!hasContent) {
		return null;
	}

	return (
		<MenuItem {...menuItemProps}>
			<Box ref={contentRef} w="100%">
				{children}
			</Box>
		</MenuItem>
	);
};

interface UserNavMenuProps {
	buttonIcon?: ReactElement;
	buttonContents?: ReactNode;
	buttonText?: string;
	children: ReactNode;
	isExpanded: boolean;
	/** When true, clicking this menu button won't close the parent menu */
	isSubmenu?: boolean;
	placement?: MenuProps['placement'];
	offset?: MenuProps['offset'];
	/** Called when the menu is opened */
	onOpen?: () => void;
}

export const UserNavMenu = ({
	buttonIcon,
	buttonContents,
	buttonText,
	children,
	isExpanded,
	isSubmenu,
	placement = 'right',
	offset,
	onOpen,
}: UserNavMenuProps) => {
	const closeAllPanels = useCloseNavbarPanels();

	const handleSubmenuClick = (e: MouseEvent) => {
		// Close all panels when menu is opened
		closeAllPanels();
		// Call onOpen callback if provided
		onOpen?.();
		// Prevent clicks from propagating to parent menu when this is a submenu
		if (isSubmenu) {
			e.stopPropagation();
		}
	};

	// Common layout styles (work for both Button and Box)
	const commonStyles = {
		px: rem(10),
		py: rem(8),
		h: rem(40),
		w: '100%',
		borderRadius: 'md',
		justifyContent: 'flex-start',
		_active: { bg: 'secondary.bgAlpha' },
		_hover: { bg: 'secondary.bgAlpha' },
		_focusVisible: { outline: '2px solid', outlineColor: 'border.focus', borderRadius: 'md', outlineOffset: '-2px' },
	};

	// Button-specific styles (only used when rendering as Button)
	const buttonStyles = {
		variant: 'ghost' as const,
		colorScheme: 'secondary' as const,
		size: 'sm' as const,
	};

	// Box-specific styles (only used when rendering as Box for submenus)
	const boxStyles = {
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
	};

	// Show tooltip when collapsed and not a submenu
	const showTooltip = !isExpanded && !isSubmenu;

	const menuButton = (
		<MenuButton
			onClick={handleSubmenuClick}
			as={isSubmenu ? Box : Button}
			{...commonStyles}
			{...(isSubmenu ? boxStyles : buttonStyles)}
			data-testid="user-nav-menu-button"
		>
			<HStack spacing={3} justifyContent="flex-start" alignItems="center" w="100%">
				{buttonIcon}
				{buttonText && (
					<Text
						variant="medium"
						whiteSpace="nowrap"
						opacity={isExpanded ? 1 : 0}
						transition={isExpanded ? 'opacity 0.15s 0.075s linear' : 'opacity 0.15s cubic-bezier(.4, 0, .2, 1)'}
					>
						{buttonText}
					</Text>
				)}
				{buttonContents && (
					<Box opacity={isExpanded ? 1 : 0} transition={isExpanded ? 'opacity 0.15s 0.075s linear' : 'opacity 0.15s cubic-bezier(.4, 0, .2, 1)'} flex={1}>
						{buttonContents}
					</Box>
				)}
			</HStack>
		</MenuButton>
	);

	const menuContent = (
		<Menu placement={placement} offset={offset}>
			{/* Always render Tooltip to prevent remounting when isExpanded changes (which breaks opacity transition) */}
			<Tooltip label={buttonText} placement="right" isDisabled={!showTooltip || !buttonText}>
				{menuButton}
			</Tooltip>
			<Portal>
				<MenuList px={rem(6)} py={rem(8)} bgColor="bg.surface" zIndex="popover">
					<VStack spacing={rem(4)} align="stretch" bgColor="bg.surface">
						{Children.toArray(children)
							// NOTE: hide any falsy children (e.g. anything that is undefined or null that the user does not have access to)
							.filter(Boolean)
							.map((child, index) => {
								// Check if the child is a submenu (UserNavMenu with isSubmenu prop)
								const isChildSubmenu = isValidElement(child) && child.props?.isSubmenu;
								// Check if the child is a divider (UserNavDivider component)
								const isChildDivider = isValidElement(child) && (child.type as FC & { isNavDivider?: boolean })?.isNavDivider;

								// Dividers render without MenuItem wrapper
								if (isChildDivider) {
									return <Box key={index}>{child}</Box>;
								}

								// For submenus, wrap in MenuItem but click the MenuButton inside to open
								if (isChildSubmenu) {
									const handleSubmenuKeyboardOpen = (e: MouseEvent<HTMLButtonElement>) => {
										if (e.detail !== 0) return;
										const menuButton = e.currentTarget.querySelector('[data-testid="user-nav-menu-button"]') as HTMLButtonElement;
										menuButton?.click();
									};

									return (
										<ConditionalMenuItem
											key={index}
											bgColor="bg.surface"
											p={0}
											_hover={{ bg: 'none', border: 'none' }}
											_focusVisible={{ bg: 'none', border: '2px solid', borderColor: 'blue.500', borderRadius: 'md', outlineOffset: '-2px' }}
											onClick={handleSubmenuKeyboardOpen}
											closeOnSelect={false}
											data-testid={`user-nav-menu-item-${index}`}
										>
											{child}
										</ConditionalMenuItem>
									);
								}

								//  NOTE: this is for keyboard navigation since the clickable element is on the child (UserNavLink or UserNavAction), not the MenuItem
								//  e.detail === 0 indicates a keyboard-triggered click (Enter/Space), while e.detail >= 1 indicates a mouse click
								//  For mouse clicks, the child's onClick already fired, so we skip to avoid duplicate navigation
								const handleClick = (e: MouseEvent<HTMLElement>) => {
									if (e.detail !== 0) return;
									// Try anchor first (UserNavLink), then button (UserNavAction)
									const clickable = e.currentTarget.querySelector('a') ?? e.currentTarget.querySelector('button');
									clickable?.click();
								};

								// Render MenuItem as Box to avoid nested button issues when child is UserNavAction (which is a button)
								return (
									<ConditionalMenuItem
										as={Box}
										key={index}
										bgColor="bg.surface"
										p={0}
										_hover={{ bg: 'none', border: 'none' }}
										_focusVisible={{ bg: 'none', border: '2px solid', borderColor: 'blue.500', borderRadius: 'md', outlineOffset: '-2px' }}
										onClick={handleClick}
										data-testid={`user-nav-menu-item-${index}`}
									>
										{child}
									</ConditionalMenuItem>
								);
							})}
					</VStack>
				</MenuList>
			</Portal>
		</Menu>
	);

	// Wrap in a Box to prevent MenuItem from treating clicks on this as a menu selection
	if (isSubmenu) {
		return (
			<Box w="100%" onClick={handleSubmenuClick}>
				{menuContent}
			</Box>
		);
	}

	return menuContent;
};
